import { useEffect, useState } from "react";

import { carePlansApi, templatesApi } from "@/api/careplans";
import { Alert } from "@/components/Alert";
import { Field } from "@/components/Field";
import { FormSelect } from "@/components/FormSelect";
import { Modal } from "@/components/Modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

function statusVariant(status) {
  if (status === "paused") return "outline";
  return "secondary";
}

const CATEGORY_OPTIONS = [
  { value: "personal_care", label: "Personal Care" },
  { value: "medication", label: "Medication" },
  { value: "mobility", label: "Mobility" },
  { value: "companionship", label: "Companionship" },
  { value: "nursing", label: "Nursing" },
  { value: "custom", label: "Custom" },
];

export function ClientCarePlansTab({ clientId }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [category, setCategory] = useState("personal_care");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState("");
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState(null);

  const loadClientPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await carePlansApi.list({ client: clientId });
      setPlans(res.results || []);
    } catch (err) {
      setError(err.message || "Failed to load client care plans.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      loadClientPlans();
    }
  }, [clientId]);

  const openAssignModal = async () => {
    setModalError(null);
    setShowAssignModal(true);
    try {
      const res = await templatesApi.list({ is_active: true });
      setTemplates(res.results || []);
    } catch {
      setModalError("Failed to load templates.");
    }
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    setModalError(null);
    setModalSaving(true);

    const payload = {
      client: clientId,
      start_date: startDate,
      end_date: endDate || null,
    };

    if (selectedTemplateId) {
      payload.template = selectedTemplateId;
    } else {
      payload.title = customTitle.trim() || "Custom Care Plan";
      payload.category = category;
    }

    try {
      await carePlansApi.create(payload);
      setShowAssignModal(false);
      setSelectedTemplateId("");
      setCustomTitle("");
      loadClientPlans();
    } catch (err) {
      setModalError(err.detail || err.message || "Failed to assign care plan.");
    } finally {
      setModalSaving(false);
    }
  };

  const activePlans = plans.filter((p) => p.status === "active");
  const inactivePlans = plans.filter((p) => p.status !== "active");

  const templateOptions = [
    { value: "", label: "-- Create Custom Care Plan --" },
    ...templates.map((tpl) => ({
      value: tpl.id,
      label: `${tpl.name} (${tpl.category_display})`,
    })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <h3 className="text-base font-semibold">Client Care Plans</h3>
          <p className="text-sm text-muted-foreground">
            Active care plans define task checklists copied during visit scheduling.
          </p>
        </div>
        <Button type="button" onClick={openAssignModal}>
          Assign Care Plan
        </Button>
      </div>

      <Alert>{error}</Alert>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading care plans...</p>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Active Care Plans ({activePlans.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activePlans.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No active care plans assigned to this client.
                </p>
              ) : (
                <div className="flex flex-col gap-4">
                  {activePlans.map((plan) => (
                    <div
                      key={plan.id}
                      className="rounded-lg border border-border bg-muted/30 p-4"
                    >
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="font-semibold">{plan.title}</div>
                        <Badge>Active</Badge>
                      </div>

                      <div className="mb-3 flex flex-wrap gap-2">
                        <Badge variant="outline">
                          {plan.category_display || plan.category}
                        </Badge>
                        <Badge variant="secondary">Start: {plan.start_date}</Badge>
                        {plan.template_name ? (
                          <Badge variant="secondary">Template: {plan.template_name}</Badge>
                        ) : null}
                      </div>

                      <div>
                        <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Care Checklist ({plan.tasks?.length || 0} tasks)
                        </p>
                        <ul className="list-disc space-y-1 pl-5 text-sm">
                          {plan.tasks?.map((t) => (
                            <li key={t.id}>
                              <strong>{t.title}</strong>
                              {t.description ? (
                                <span className="text-muted-foreground"> — {t.description}</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {inactivePlans.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Plan History ({inactivePlans.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-3">
                  {inactivePlans.map((plan) => (
                    <div
                      key={plan.id}
                      className="flex items-center justify-between gap-3 rounded-lg bg-muted/30 px-3 py-2.5"
                    >
                      <div>
                        <div className="text-sm font-semibold">{plan.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {plan.category_display} • Started {plan.start_date}
                        </div>
                      </div>
                      <Badge variant={statusVariant(plan.status)} className="capitalize">
                        {plan.status_display || plan.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : null}
        </>
      )}

      {showAssignModal ? (
        <Modal title="Assign Care Plan to Client" onClose={() => setShowAssignModal(false)}>
          <Alert>{modalError}</Alert>

          <form onSubmit={handleCreatePlan} className="space-y-4">
            <Field label="Select Template or Custom" name="template">
              {(props) => (
                <FormSelect
                  id={props.id}
                  name={props.name}
                  value={selectedTemplateId}
                  onValueChange={setSelectedTemplateId}
                  options={templateOptions}
                  placeholder="Select template…"
                />
              )}
            </Field>

            {!selectedTemplateId ? (
              <>
                <Field label="Plan Title" name="custom_title">
                  {(props) => (
                    <Input
                      {...props}
                      type="text"
                      placeholder="e.g. Daily Mobility Support Plan"
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      required
                    />
                  )}
                </Field>

                <Field label="Category" name="category">
                  {(props) => (
                    <FormSelect
                      id={props.id}
                      name={props.name}
                      value={category}
                      onValueChange={setCategory}
                      options={CATEGORY_OPTIONS}
                      placeholder="Select category…"
                    />
                  )}
                </Field>
              </>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Start Date" name="start_date">
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                )}
              </Field>

              <Field label="End Date (optional)" name="end_date">
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                )}
              </Field>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button type="submit" disabled={modalSaving}>
                {modalSaving ? "Assigning..." : "Assign Care Plan"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAssignModal(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}
